<template>
  <div>
    <div id="call-records-view" class="page-table scrollable">
      <el-dialog
        width="440px"
        :visible="modalVisible"
        :before-close="handleCloseModal"
        :close-on-click-modal="false"
      >
        <h2 class="form-section-header">Filter Settings</h2>

        <h3 class="black-text mb-5">Select Date Range</h3>
        <el-date-picker
          class="mb-10"
          style="margin-left: auto"
          v-model="historyDateRange"
          type="datetimerange"
          align="right"
          unlink-panels
          format="yyyy-MM-dd HH:mm"
          :clearable="false"
          range-separator="To"
          start-placeholder="Start date"
          end-placeholder="End date"
          :picker-options="datePickerOptions"
        >
        </el-date-picker>

        <el-form id="stats-advanced-modal" class="form-modal">
          <h3 class="black-text mb-10">Select Distribution</h3>
          <el-radio-group v-model="distribution">
            <el-radio-button type="default" label="daily"
              >Daily</el-radio-button
            >
            <el-radio-button label="monthly">Monthly</el-radio-button>
            <el-radio-button label="none">None</el-radio-button>
          </el-radio-group>

          <h3 class="black-text mb-0">Filter By</h3>
          <el-select
            v-model="primaryFilter"
            placeholder="Select Filter"
            clearable
            style="margin-top: 0"
          >
            <template slot="prefix"> Filter By </template>
            <el-option value="branch" label="Branch" />
            <el-option value="numbers" label="Numbers" />
          </el-select>

          <dads-select
            v-if="primaryFilter === 'branch'"
            v-model="branch"
            placeholder="Select A Branch"
            filterable
          >
            <el-option
              v-for="(item, index) in branchOptions"
              :key="index"
              :label="item.name"
              :value="item.id"
            />
            <template slot="prefix"> Filter By Branch </template>
          </dads-select>

          <dads-select
            v-if="primaryFilter === 'numbers'"
            v-model="numbers"
            multiple
            placeholder="Select Numbers"
            remote
            filterable
            clearable
            :remote-method="searchNumbers"
          >
            <el-option
              v-for="(item, index) in numberOptions"
              :key="item.key"
              :label="item.formattedNumber"
              :value="item.number"
            />
          </dads-select>

          <el-button class="mt-20" @click="doAdvanced"> Update </el-button>
        </el-form>
      </el-dialog>

      <div id="users-stats-view" class="no-table-box page-header">
        <div class="flex justify-space-between mb-20">
          <h2 class="mb-0 mt-0 text-left">
            <v-svg sprite="phone-number" class="voxo-pink"></v-svg>
            Phone Numbers
          </h2>
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px"></i>
            Total Numbers: {{ total }}
          </el-tag>
        </div>

        <div class="flex align-center mb-20">
          <el-radio-group
            v-model="presetSelection"
            @change="doPreset"
            class="hidden-xs-only"
          >
            <el-radio-button type="default" label="yesterday"
              >Yesterday</el-radio-button
            >
            <el-radio-button type="default" label="last10"
              >Last 10 Days</el-radio-button
            >
            <el-radio-button label="lastMonth">Last 30 Days</el-radio-button>
          </el-radio-group>

          <el-tooltip placement="top" :content="selectedDateRange">
            <el-button @click="modalVisible = true" class="ml-10">
              Filter
            </el-button>
          </el-tooltip>
          <el-button
            @click="resetTable"
            v-if="advancedActive"
            style="background-color: #f25a6b; color: #fff"
          >
            Reset
          </el-button>
          <el-dropdown class="ml-10">
            <el-button>
              Export
              <i class="el-icon-arrow-down el-icon--right"></i>
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item @click.native="doExport">
                <v-svg class="success-text" sprite="list" />
                CSV
              </el-dropdown-item>
              <el-dropdown-item @click.native="openScheduledReport">
                <v-svg class="voxo-orange" sprite="clock" />
                Schedule Report
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>

        <div class="vxe-table-height-tabs card-base card-shadow--medium">
          <vxe-grid
            round
            class="user-stats-table"
            border="inner"
            ref="xGrid"
            height="100%"
            auto-resize
            empty-text="No Data"
            :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
            :loading="loadingData"
            :columns="
              this.distribute
                ? [this.expandColumn, ...this.tableColumns]
                : this.tableColumns
            "
          >
          </vxe-grid>
        </div>
      </div>
    </div>

    <schedule-report-modal
      :modalVisible="scheduleReportVisible"
      :filterSettings="filterSettings"
      @cancelOperation="scheduleReportVisible = false"
    />
  </div>
</template>

<script>
import moment from "moment-timezone";
import debounce from "lodash/debounce";
import { ExportToCsv } from "export-to-csv";
import DadsSelect from "@/components/select";
import feathersClient from "@/feathers/feathers-client";
import { phoneNumberFormatter } from "@/globals/globals";
import momentDurationFormat from "moment-duration-format";
import ScheduleReportModal from "@/components/schedule-reports/ScheduleReportModal.vue";

export default {
  name: "NumberStatsData",
  props: {
    branchOptions: { required: true },
  },

  components: { DadsSelect, ScheduleReportModal },

  data() {
    return {
      scheduleReportVisible: false,
      filterSettings: null,
      queryParams: null,

      loadingData: false,
      modalVisible: false,
      tableData: [],
      presetSelection: "last10",
      advancedActive: false,
      primaryFilter: "",
      numbers: [],
      branch: "",
      distribution: "none",
      exportCSV: false,
      numberOptions: [],
      page: 1,
      pages: 0,
      limit: 50,
      total: 0,
      startDate: null,
      endDate: null,
      distribute: false,
      historyDateRange: [moment(), moment()],
      datePickerOptions: {
        shortcuts: [
          {
            text: "Last week",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "week").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last month",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "month").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 3 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(3, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 6 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(6, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last year",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(12, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
        ],
      },
      expandColumn: {
        type: "expand",
        width: "25px",
        showOverflow: true,
        slots: {
          content: ({ row }) => {
            return [
              <div class="users-stats-distribution-table">
                <vxe-table
                  border="none"
                  show-overflow
                  highlight-hover-row
                  show-header={false}
                  height="300"
                  empty-text="No Data"
                  scroll-y={{ enabled: true, gt: 10, oSize: 0 }}
                  data={row.distributionStats}
                >
                  <vxe-table-column
                    field="number"
                    width="135"
                  ></vxe-table-column>
                  <vxe-table-column field="name" width="250"></vxe-table-column>
                  <vxe-table-column
                    field="inboundAnswered"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="inboundNoAnswer"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="inboundBusy"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="inboundFailed"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="totalTalkTime"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                  <vxe-table-column
                    field="totalInbound"
                    min-width="100"
                  ></vxe-table-column>
                  <vxe-table-column
                    field="avgTalkTime"
                    min-width="100"
                    formatter={this.timeFormatterTable}
                  ></vxe-table-column>
                </vxe-table>
              </div>,
            ];
          },
        },
      },
      tableColumns: [
        {
          field: "number",
          title: "Number",
          width: "135",
          slots: {
            default: ({ row }) => {
              return [
                <span class="first-table-column">
                  {this.numberFormatter(row.number)}
                </span>,
              ];
            },
          },
        },
        {
          field: "name",
          title: "Name",
          width: "250",
          sortable: true,
          showOverFlow: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.name}</span>];
            },
          },
        },
        {
          field: "inboundAnswered",
          title: "Answered",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.inboundAnswered}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Inbound Answered">
                  <v-svg sprite="call-inbound" class="voxo-green"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "inboundNoAnswer",
          title: "Inbound No Answer",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.inboundNoAnswer}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Inbound No Answer">
                  <v-svg sprite="call-inbound" class="voxo-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "inboundBusy",
          title: "Inbound Busy",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.inboundBusy}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Inbound Busy">
                  <v-svg sprite="call-pending" class="voxo-orange"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "inboundFailed",
          title: "Inbound Failed",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.inboundFailed}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Inbound Failed">
                  <v-svg sprite="call-block" class="voxo-dark-red"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "totalTalkTime",
          title: "Total Talk Time",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.totalTalkTime)}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Total Talk Time">
                  <v-svg sprite="clock" class="voxo-aqua"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "totalInbound",
          title: "Inbound Total",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{row.totalInbound}</span>];
            },
            header: () => {
              return [
                <el-tooltip placement="top" content="Inbound Total">
                  <v-svg sprite="arrow-inbound" class="voxo-green"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "avgTalkTime",
          title: "Average Talk Time",
          minWidth: "100",
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.avgTalkTime)}</span>];
            },
            header: () => {
              return [
                <el-tooltip
                  placement="top"
                  content="Average Talk Time Per Call"
                >
                  <v-svg sprite="clock" class="voxo-purple"></v-svg>
                </el-tooltip>,
              ];
            },
          },
        },
      ],
    };
  },
  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    userOffsetMinutes() {
      return this.$store.state.userTimeOffset;
    },
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    selectedDateRange() {
      if (this.endDate && this.startDate) {
        const dateStart = moment(this.startDate).format("M/D/YY HH:mm");
        const dateEnd = moment(this.endDate).format("M/D/YY HH:mm");
        return `${dateStart} - ${dateEnd}`;
      }
    },
  },
  watch: {
    page: debounce(function () {
      this.queryStats();
    }, 500),
    selectedTenant() {
      this.resetTable();
    },

    queryParams(params) {
      if (params) {
        const {
          type,
          tenantId,
          timezone,
          primaryFilter,
          branch,
          distribution,
          numbers,
        } = params;

        const queryParams = {
          type,
          queryType: "callStatistics",
          tenantId: {
            label: this.selectedTenant.name,
            value: tenantId,
          },
          timezone,
          primaryFilter: {
            label: "",
            value: "",
          },
          branch: { label: "", value: "" },
          distribution,
          numbers: [],
        };

        if (primaryFilter) {
          const primaryFilterMap = {
            branch: "Branch",
            numbers: "Numbers",
          };

          queryParams["primaryFilter"] = {
            label: primaryFilterMap[primaryFilter],
            value: primaryFilter,
          };
        }

        if (branch) {
          const { name, id } = this.branchOptions.find((b) => branch === b.id);
          queryParams["branch"] = { label: name, value: id };
        }

        if (numbers.length) {
          queryParams["numbers"] = numbers.map((num) => {
            return { label: this.numberFormatter(num), value: num };
          });
        }

        this.filterSettings = {
          servicePath: "account-statistics",
          module: "callStatistics",
          query: queryParams,
        };
      }
    },
  },

  methods: {
    openScheduledReport() {
      this.scheduleReportVisible = true;
    },

    pageNext() {
      if (this.page < this.pages) {
        this.page += 1;
      }
    },
    pagePrevious() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },
    pageSet(page) {
      this.page = page;
    },
    handleCloseModal() {
      this.modalVisible = false;
    },
    doPreset(val) {
      if (val === "init") {
        this.endDate = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment().startOf("day").format("YYYY-MM-DD");
        this.presetSelection = "init";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "last10") {
        this.endDate = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment().subtract(9, "days").format("YYYY-MM-DD");
        this.presetSelection = "last10";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "lastMonth") {
        this.endDate = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment().subtract(30, "days").format("YYYY-MM-DD");
        this.presetSelection = "lastMonth";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "yesterday") {
        this.endDate = moment()
          .startOf("day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment()
          .subtract(1, "day")
          .startOf("day")
          .format("YYYY-MM-DD HH:mm");
        this.presetSelection = "yesterday";
        this.updateDateRangePicker();
        this.queryStats();
      }
    },
    doAdvanced() {
      this.advancedActive = true;
      this.startDate = moment(this.historyDateRange[0]).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      this.endDate = moment(this.historyDateRange[1]).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      this.updateDateRangePicker();
      this.queryStats();
      this.handleCloseModal();
      this.distribute = this.distribution !== "none";
      this.presetSelection = null;
    },
    resetTable() {
      this.advancedActive = false;
      this.primaryFilter = "";
      this.numbers = [];
      this.exportCSV = false;
      this.branch = "";
      this.distribution = "none";
      this.page = 1;
      this.distribute = false;
      this.doPreset("init");
    },
    queryStats() {
      this.loadingData = true;
      this.queryParams = {
        type: "phoneNumbers",
        tenantId: this.selectedTenant.id,
        timezone: this.user.timezone,
        primaryFilter: this.primaryFilter,
        numbers: this.numbers,
        branch: this.branch,
        distribution: this.distribution,
      };

      feathersClient
        .service("account-statistics")
        .create({
          dateStart: this.startDate,
          dateEnd: this.endDate,
          exportCSV: this.exportCSV,
          $skip: this.page > 1 ? this.limit * (this.page - 1) : 0,
          ...this.queryParams,
        })
        .then((res) => {
          //update table if not exporting
          if (!this.exportCSV) {
            this.total = res.total;
            this.tableData = res.data;
            this.pages = Math.ceil(res.total / this.limit);
            this.page = this.page > this.pages ? 1 : this.page;
            this.populateTable();
          }

          this.exportCSV = false;
          this.loadingData = false;
        })
        .catch(() => {
          this.loadingData = false;
        });
    },
    updateDateRangePicker() {
      this.historyDateRange = [moment(this.startDate), moment(this.endDate)];
    },
    timeFormatter(value) {
      return moment.duration(value, "seconds").format("HH:mm:ss", {
        trim: false,
      });
    },
    timeFormatterTable(data) {
      return this.timeFormatter(data.cellValue);
    },
    searchNumbers(query) {
      if (query.length) {
        const searchTerm = (query + "")
          .replace(/[\\"']/g, "\\$&")
          .replace(/\u0000/g, "\\0");
        feathersClient
          .service("dids")
          .find({
            query: {
              tenantId: this.selectedTenant.id,
              number: { $like: `%${searchTerm}%` },
              $limit: 25,
            },
          })
          .then((res) => {
            this.numberOptions = res.map((item) => {
              return {
                key: this.getRandomKey(),
                formattedNumber: this.numberFormatterSelect(item.number),
                number: item.number,
              };
            });
          });
      }
    },
    getRandomKey() {
      const randomString = () => Math.random().toString(36).slice(2);
      return `${randomString()}-${randomString()}$${randomString()}`;
    },
    doExport() {
      this.$confirm(`Export data for ${this.total} numbers?`, {
        type: "warning",
      })
        .then(() => {
          //export from browser
          let csvData = [];
          for (let i = 0; i < this.tableData.length; i++) {
            const csvObject = {};
            for (let key in this.tableData[i]) {
              if (key !== "distributionStats" && key !== "_XID") {
                csvObject[key] = this.tableData[i][key];
              }
            }
            csvData.push(csvObject);

            const distStats = this.tableData[i].distributionStats;
            if (distStats) {
              for (let k = 0; k < distStats.length; k++) {
                const csvObjectDist = {};
                csvObjectDist["number"] = "";
                for (let key in distStats[k]) {
                  csvObjectDist[key] = distStats[k][key];
                }
                if (Object.keys(csvObjectDist).length)
                  csvData.push(csvObjectDist);
              }
            }
          }

          const options = {
            filename: "phone_number_statistics",
            showLabels: true,
            headers: [
              "Number",
              "Name",
              "Inbound Answered",
              "Inbound No Answer",
              "Inbound Busy",
              "Inbound Failed",
              "Total Talk Time",
              "Inbound Total",
              "Average Talk Time Per Call",
            ],
          };

          const csvExporter = new ExportToCsv(options);
          csvExporter.generateCsv(csvData);
        })
        .catch(() => true);
    },
    numberFormatter(num) {
      return phoneNumberFormatter(num);
    },
    numberFormatterSelect(number) {
      return phoneNumberFormatter(number);
    },
    populateTable() {
      if (this.$refs.xGrid) {
        this.$refs.xGrid.loadData(this.tableData);
        const arrowDown = document.querySelectorAll(".vxe-sort--desc-btn");
        const arrowUp = document.querySelectorAll(".vxe-sort--asc-btn");
        const arrows = [...arrowDown, ...arrowUp];
        for (let i = 0; i < arrows.length; i++) {
          arrows[i].removeAttribute("title");
        }
      }
    },
  },
  mounted() {
    this.doPreset("init");
  },
};
</script>
